import { z } from "zod";

export const JobStatusSchema = z.enum([
  "PENDING",
  "PENDING_HELD",
  "PROCESSING",
  "PROCESSING_STOPPED",
  "CANCELED",
  "ABORTED",
  "COMPLETED",
]);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const MachineJobResponseSchema = z.object({
  id: z.number(),
  index: z.number(),
  machineId: z.number(),
  jobName: z.string().nullable().optional(),
  startTime: z.string(),
  completeTime: z.string().nullable().optional(),
  status: JobStatusSchema,
});

export type MachineJobResponse = z.infer<typeof MachineJobResponseSchema>;

export const GetJobStatisticsRequestSchema = z.object({
  apiKey: z.string(),
  machineId: z.number(),
  startDate: z.string(),
  endDate: z.string(),
});

export type GetJobStatisticsRequest = z.infer<
  typeof GetJobStatisticsRequestSchema
>;

export const JobStaticsResponseSchema = z.object({
  date: z.string(),
  paymentCount: z.number(),
  payerCount: z.number(),
  paymentAmount: z.number(),
});

export type JobStaticsResponse = z.infer<typeof JobStaticsResponseSchema>;

export const GetJobByMachineRequestSchema = z.object({
  apiKey: z.string(),
  machineId: z.number(),
  jobId: z.number(),
});

export type GetJobByMachineRequest = z.infer<
  typeof GetJobByMachineRequestSchema
>;

export class MachineJobEntity implements MachineJobResponse {
  readonly id: number;
  readonly index: number;
  readonly machineId: number;
  readonly jobName?: string | null;
  readonly startTime: string;
  readonly completeTime?: string | null;
  readonly status: JobStatus;

  constructor(data: MachineJobResponse) {
    const validated = MachineJobResponseSchema.parse(data);

    this.id = validated.id;
    this.index = validated.index;
    this.machineId = validated.machineId;
    this.jobName = validated.jobName;
    this.startTime = validated.startTime;
    this.completeTime = validated.completeTime;
    this.status = validated.status;
  }

  isCompleted(): boolean {
    return this.completeTime !== null && this.completeTime !== undefined;
  }

  isInProgress(): boolean {
    return !this.isCompleted();
  }

  getDuration(): number | null {
    if (!this.completeTime) return null;

    const start = new Date(this.startTime);
    const end = new Date(this.completeTime);
    return end.getTime() - start.getTime();
  }

  getDurationInMinutes(): number | null {
    const duration = this.getDuration();
    return duration ? Math.floor(duration / (1000 * 60)) : null;
  }

  getDisplayName(): string {
    return this.jobName || `Job ${this.id}`;
  }

  toJSON(): MachineJobResponse {
    return {
      id: this.id,
      index: this.index,
      machineId: this.machineId,
      jobName: this.jobName,
      startTime: this.startTime,
      completeTime: this.completeTime,
      status: this.status,
    };
  }

  static fromApiResponse(data: any): MachineJobEntity {
    return new MachineJobEntity(data);
  }

  static validate(data: unknown): MachineJobResponse {
    return MachineJobResponseSchema.parse(data);
  }
}
